import { create } from 'zustand'
import type { Table } from '@/types'
import { getTableHeight, getTableWidth } from '@/utils/geometry'

// ── Constants ──────────────────────────────────────────────────────────────────
export const SCALE_MIN     = 0.08
export const SCALE_MAX     = 3.0
export const SCALE_DEFAULT = 1.0

// Multiplicative step for zoomIn/zoomOut buttons (15%)
const ZOOM_STEP = 0.15

// Padding around content when fitting all tables in viewport
const FIT_PADDING = 60

// ── Types ─────────────────────────────────────────────────────────────────────
export interface ViewportSize {
  width:  number
  height: number
}

interface ViewportState {
  /** Current zoom scale (clamped to [SCALE_MIN, SCALE_MAX]) */
  sc: number
  /** Pan X offset — used in CSS: transform: scale(sc) translate(tx, ty) */
  tx: number
  /** Pan Y offset */
  ty: number
  /** Exposed min/max for UI controls */
  minZoom: number
  maxZoom: number

  /** Canvas container dimensions. Updated by Canvas via ResizeObserver. */
  containerWidth:  number
  containerHeight: number

  // ── Zoom actions ─────────────────────────────────────────────────────────
  /** Set absolute zoom value (clamped). */
  setZoom: (value: number) => void
  /** Zoom in by ZOOM_STEP factor. */
  zoomIn: () => void
  /** Zoom out by ZOOM_STEP factor. */
  zoomOut: () => void
  /** Reset zoom and pan to defaults. */
  resetZoom: () => void

  // ── Pan actions ───────────────────────────────────────────────────────────
  /** Set absolute pan position. */
  setPan: (tx: number, ty: number) => void
  /** Move pan by a relative amount. */
  panBy: (dx: number, dy: number) => void

  // ── Combined ──────────────────────────────────────────────────────────────
  /** Set scale and pan together (atomically). */
  setViewport: (sc: number, tx: number, ty: number) => void

  /**
   * Zoom keeping a screen point (centerX, centerY) stationary.
   * Used for scroll-wheel zoom in the Canvas component.
   *
   * @param delta  - fractional delta (e.g. 0.1 or -0.1)
   * @param centerX - screen X of the zoom anchor (default 0)
   * @param centerY - screen Y of the zoom anchor (default 0)
   */
  zoom: (delta: number, centerX?: number, centerY?: number) => void

  /**
   * Fit all tables in the viewport.
   *
   * NOTE: This is a pure calculation — `viewportSize` must be supplied by the
   * caller (Canvas component reads it from the DOM in T-19/T-21). Keeping the
   * action DOM-free to make it testable without a browser.
   *
   * If `tables` is empty the call is a no-op.
   */
  fitAll: (tables: Table[], viewportSize: ViewportSize) => void

  /**
   * Convert a screen (client) coordinate to canvas coordinate.
   *
   * NOTE: This overload takes the canvas element's bounding rect so that the
   * calculation remains pure (no DOM access inside the store). The Canvas
   * component will supply the rect. Full DOM integration happens in T-11.
   *
   * Canvas coord = (clientX - rect.left) / sc - tx
   */
  clientToCanvas: (
    clientX: number,
    clientY: number,
    canvasRect: { left: number; top: number }
  ) => { x: number; y: number }

  /**
   * Store the canvas container size (set by Canvas via ResizeObserver).
   * Used by resetView so it can call fitAll without DOM access.
   */
  setContainerSize: (width: number, height: number) => void

  /**
   * Fit all tables in the viewport, or reset to defaults if no tables.
   * Equivalent to legacy resetView() / btn-zr behaviour.
   */
  resetView: (tables: Table[]) => void

  // ── Legacy aliases (used by code still in migration) ─────────────────────
  /** @deprecated Use setZoom */
  setScale: (sc: number) => void
  /** @deprecated Use resetZoom */
  reset: () => void
}

// ── Store ─────────────────────────────────────────────────────────────────────
export const useViewportStore = create<ViewportState>()((set, get) => ({
  sc:      SCALE_DEFAULT,
  tx:      0,
  ty:      0,
  minZoom: SCALE_MIN,
  maxZoom: SCALE_MAX,
  containerWidth:  0,
  containerHeight: 0,

  // ── Zoom ──────────────────────────────────────────────────────────────────

  setZoom: (value) =>
    set({ sc: Math.min(SCALE_MAX, Math.max(SCALE_MIN, value)) }),

  zoomIn: () => {
    const { sc } = get()
    set({ sc: Math.min(SCALE_MAX, sc * (1 + ZOOM_STEP)) })
  },

  zoomOut: () => {
    const { sc } = get()
    set({ sc: Math.max(SCALE_MIN, sc * (1 - ZOOM_STEP)) })
  },

  resetZoom: () => set({ sc: SCALE_DEFAULT, tx: 0, ty: 0 }),

  // ── Pan ───────────────────────────────────────────────────────────────────

  setPan: (tx, ty) => set({ tx, ty }),

  panBy: (dx, dy) =>
    set((s) => ({ tx: s.tx + dx, ty: s.ty + dy })),

  // ── Combined ──────────────────────────────────────────────────────────────

  setViewport: (sc, tx, ty) =>
    set({ sc: Math.min(SCALE_MAX, Math.max(SCALE_MIN, sc)), tx, ty }),

  zoom: (delta, centerX = 0, centerY = 0) => {
    const { sc, tx, ty } = get()
    const newSc = Math.min(SCALE_MAX, Math.max(SCALE_MIN, sc + delta * sc))
    const ratio = newSc / sc
    set({
      sc: newSc,
      tx: centerX - ratio * (centerX - tx),
      ty: centerY - ratio * (centerY - ty),
    })
  },

  fitAll: (tables, viewportSize) => {
    if (!tables.length) return

    const pad   = FIT_PADDING
    const { width, height } = viewportSize

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    for (const t of tables) {
      const tW = getTableWidth(t)
      const tH = getTableHeight(t)
      if (t.x           < minX) minX = t.x
      if (t.y           < minY) minY = t.y
      if (t.x + tW      > maxX) maxX = t.x + tW
      if (t.y + tH      > maxY) maxY = t.y + tH
    }

    const contentW = Math.max(1, maxX - minX)
    const contentH = Math.max(1, maxY - minY)

    const scaleX = (width  - pad * 2) / contentW
    const scaleY = (height - pad * 2) / contentH
    const newSc  = Math.min(SCALE_MAX, Math.max(SCALE_MIN, Math.min(scaleX, scaleY)))

    // CSS transform: scale(sc) translate(tx, ty)
    // Screen point of canvas coord (cX, cY) = sc*(cX + tx), sc*(cY + ty)
    // To center the content mid-point at (width/2, height/2):
    //   newSc*(centerX + newTx) = width/2  →  newTx = width/(2*newSc) - centerX
    const centerX = minX + contentW / 2
    const centerY = minY + contentH / 2
    const newTx   = width  / (2 * newSc) - centerX
    const newTy   = height / (2 * newSc) - centerY

    set({ sc: newSc, tx: newTx, ty: newTy })
  },

  clientToCanvas: (clientX, clientY, canvasRect) => {
    const { sc, tx, ty } = get()
    return {
      x: (clientX - canvasRect.left) / sc - tx,
      y: (clientY - canvasRect.top)  / sc - ty,
    }
  },

  // ── Container size ────────────────────────────────────────────────────────

  setContainerSize: (width, height) => set({ containerWidth: width, containerHeight: height }),

  resetView: (tables) => {
    const { containerWidth, containerHeight, fitAll, resetZoom } = get()
    if (!tables.length || !containerWidth || !containerHeight) {
      resetZoom()
      return
    }
    fitAll(tables, { width: containerWidth, height: containerHeight })
  },

  // ── Legacy aliases ────────────────────────────────────────────────────────

  setScale: (sc) =>
    set({ sc: Math.min(SCALE_MAX, Math.max(SCALE_MIN, sc)) }),

  reset: () => set({ sc: SCALE_DEFAULT, tx: 0, ty: 0 }),
}))
